import { dateOnly } from "./leaseAreaBasis.js";

export const RENT_RECONCILIATION_TOLERANCE = 0.01;
const known = (value) => value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);

// Recorded segments make detail relevant even if they contain gaps, overlaps or
// unsupported boundaries. Invalid dates remain visible to the strict calculator.
export function hasRentSegmentsForYear(areas, year) {
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  return (areas || []).some((area) => (area.rentRates || []).some((rate) => {
    const start = dateOnly(rate.effectiveFrom);
    const end = dateOnly(rate.effectiveTo);
    return (!start || start <= to) && (!end || end >= from);
  }));
}

// Contractual activity is sourced independently of parent term/occupancy. There
// is deliberately no annual-anchor fallback, partial-month proration or renewal.
export function contractualAreaMonth(areas, year, month, nativeCurrency) {
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const to = dateOnly(new Date(Date.UTC(year, month, 0)));
  const issues = [];
  const contributions = [];
  for (const area of areas || []) {
    const start = dateOnly(area.commencementDate);
    const end = dateOnly(area.expirationDate);
    if ((start && start > to) || (end && end < from)) continue;
    if (!start || !end) {
      issues.push(`${area.name}: area activity dates not recorded`);
      continue;
    }
    if (start > from || end < to) {
      issues.push(`${area.name}: partial-month area activity is unsupported`);
      continue;
    }
    const overlapping = (area.rentRates || []).filter((rate) =>
      dateOnly(rate.effectiveFrom) <= to && dateOnly(rate.effectiveTo) >= from);
    if (overlapping.length !== 1) {
      issues.push(`${area.name}: ${overlapping.length ? "overlapping rates" : "rate coverage missing"}`);
      continue;
    }
    const rate = overlapping[0];
    const annual = known(rate.annualRent);
    if (dateOnly(rate.effectiveFrom) > from || dateOnly(rate.effectiveTo) < to) {
      issues.push(`${area.name}: partial-month rate coverage is unsupported`);
    } else if (nativeCurrency && rate.currency && rate.currency !== nativeCurrency) {
      issues.push(`${area.name}: rate currency differs from lease currency`);
    } else if (annual == null || annual < 0 || !rate.sourceCitation?.trim() || !rate.provenance?.trim()) {
      issues.push(`${area.name}: sourced rent not recorded`);
    } else {
      const sqft = known(area.sizeSf);
      contributions.push({ areaId: area.id, name: area.name, rateId: rate.id,
        annualRent: annual, monthlyRent: annual / 12, grossSf: sqft,
        annualizedPsf: sqft > 0 ? annual / sqft : null,
        sourceCitation: rate.sourceCitation, provenance: rate.provenance });
    }
  }
  if (!contributions.length && !issues.length) issues.push("No verified contractual area activity for this month");
  const complete = issues.length === 0;
  const annualizedRent = complete ? contributions.reduce((sum, area) => sum + area.annualRent, 0) : null;
  const grossSf = complete && contributions.every((area) => area.grossSf > 0)
    ? contributions.reduce((sum, area) => sum + area.grossSf, 0) : null;
  return { year, month, from, to, complete, issues, contributions,
    contractual: complete ? annualizedRent / 12 : null, annualizedRent, grossSf,
    annualizedPsf: grossSf > 0 ? annualizedRent / grossSf : null };
}

export function contractualAreaYear(areas, year, anchor, anchorCurrency, nativeCurrency) {
  const months = Array.from({ length: 12 }, (_, index) => contractualAreaMonth(areas, year, index + 1, nativeCurrency));
  const complete = months.every((month) => month.complete);
  const detailSum = complete ? months.reduce((sum, month) => sum + month.contractual, 0) : null;
  const recordedAnchor = known(anchor);
  const currencyMatches = anchorCurrency === nativeCurrency;
  const delta = complete && recordedAnchor != null && currencyMatches ? detailSum - recordedAnchor : null;
  const status = !complete ? "incomplete" : recordedAnchor == null ? "anchor_missing"
    : !currencyMatches ? "currency_mismatch"
      : Math.abs(delta) <= RENT_RECONCILIATION_TOLERANCE + 1e-9 ? "matched" : "mismatch";
  return { year, currency: nativeCurrency, months, complete,
    reconciliation: { status, anchor: recordedAnchor, anchorCurrency, detailSum, delta,
      tolerance: RENT_RECONCILIATION_TOLERANCE, completeMonths: months.filter((month) => month.complete).length } };
}
